import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { Controller, useFormState } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { getNestedError } from 'hooks';

export const NumericFormatCustom = forwardRef(
    (
        {
            classNameWrapper,
            name,
            placeholder,
            label,
            labelHeader,
            thousandSeparator,
            fixedDecimalScale,
            decimalScale,
            prefix,
            suffix,
            control,
            rules,
            selectedValue = 'floatValue',
            handleChange,
            isAllowed,
            showError = true,
            maxLength,
            disabled,
            dontShowError,
        },
        ref,
    ) => {
        const [isFocused, setIsFocused] = useState(false);
        const inputRef = useRef(null);
        const prevHasError = useRef(false);
        const { errors } = useFormState({ control });
        const error = getNestedError(errors, name);
        const hasError = !!error?.type;

        useEffect(() => {
            if (hasError && !prevHasError.current && inputRef.current) {
                inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            prevHasError.current = hasError;
        }, [hasError]);

        return (
            <Controller
                control={control}
                name={name}
                rules={rules}
                render={({ field, formState: { errors } }) => {
                    const allowFields = { ...field };
                    if (allowFields?.onChange) delete allowFields?.onChange;

                    return (
                        <div
                            className={`numeric-format-custom-wrapper ${
                                classNameWrapper ? classNameWrapper : ''
                            }`}
                        >
                            {labelHeader && (
                                <p className={`numeric-autocomplete-label`}>
                                    {`${labelHeader}${rules?.required ? '*' : ''}`}
                                </p>
                            )}

                            {label && (
                                <p
                                    className={
                                        `numeric-autocomplete-label-mui 
                  ${(isFocused || field?.value || field?.value === 0) ? 'numeric-autocomplete-label-mui-Focused' : ''}
                  ${disabled ? 'numeric-autocomplete-label-mui-disabled' : ''}
                  `}
                                    style={{
                                        color: isFocused ? '#ba43b1' : '',
                                    }}
                                    onClick={() => {
                                        if (!disabled) {
                                            setIsFocused(!isFocused);
                                            inputRef?.current?.focus?.();
                                        }
                                    }}
                                >
                                    {`${label}${rules?.required ? '*' : ''}`}
                                </p>
                            )}
                            <NumericFormat
                                className={`numeric-format-input ${
                                    errors[field?.name]?.message
                                        ? 'numeric-format-input-error'
                                        : ''
                                }`}
                                disabled={disabled}
                                getInputRef={(el) => {
                                    ref?.(el);
                                    field.ref(el);
                                    inputRef.current = el;
                                }}
                                {...allowFields}
                                onValueChange={(formats) => {
                                    const changedFormat = formats[selectedValue];
                                    if (changedFormat === undefined || changedFormat === '') {
                                        handleChange ? handleChange('') : field.onChange('');
                                    } else {
                                        handleChange
                                            ? handleChange(changedFormat)
                                            : field.onChange(changedFormat);
                                    }
                                }}
                                thousandSeparator={thousandSeparator}
                                fixedDecimalScale={fixedDecimalScale}
                                decimalSeparator='.'
                                allowNegative={false}
                                maxLength={maxLength}
                                isNumericString
                                isAllowed={isAllowed}
                                prefix={prefix ? prefix : ''}
                                suffix={suffix ? suffix : ''}
                                placeholder={placeholder}
                                decimalScale={decimalScale}
                                onFocus={(e) => {
                                    setIsFocused(true);
                                }}
                                onBlur={(e) => {
                                    field?.onBlur(e);
                                    setIsFocused(false);
                                }}
                            />
                            {showError && (
                                <p className='error-message'>{error?.message}</p>
                            )}
                        </div>
                    );
                }}
            />
        );
    },
);

NumericFormatCustom.displayName = 'NumericFormatCustom';
